import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import TripTable from "./components/TripTable";
import StatCard from "./components/StatCard";
import DateRangeField from "./components/DateRangeField";
import { SunIcon, MoonIcon, SearchIcon } from "./components/icons";
import { computeTripStats } from "./utils/tripStats";
import "./App.css";

// ✅ Automatically use the correct backend URL
const API_URL = process.env.NODE_ENV === "development"
  ? "http://localhost:5001"
  : "https://amin-backend.onrender.com";

const STATUS_FILTERS = [
  { label: "All", value: "" },
  { label: "Completed", value: "Completed" },
  { label: "Cancelled", value: "Canceled" },
  { label: "No show", value: "NoShow" },
];

const App = () => {
  const [driverName, setDriverName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [status, setStatus] = useState("");
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [dateError, setDateError] = useState("");
  const [fetchError, setFetchError] = useState("");
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem("amin-theme");
    if (saved) return saved;
    return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  });

  useEffect(() => {
    localStorage.setItem("amin-theme", theme);
  }, [theme]);

  const tripList = useMemo(() => (Array.isArray(trips.trips) ? trips.trips : []), [trips]);
  const stats = useMemo(() => computeTripStats(tripList), [tripList]);

  // ✅ Fetch trips with filters
  const fetchTrips = async () => {
    if (startDate && endDate && endDate < startDate) {
      setDateError("End date cannot be before start date.");
      return;
    }
    setDateError("");
    setFetchError("");
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/trips`, {
        params: { driverName, startDate, endDate, status },
        headers: { "x-api-key": process.env.REACT_APP_API_SECRET },
      });
      setTrips(response.data);
      setHasSearched(true);
    } catch (error) {
      console.error("Error fetching trips:", error);
      setFetchError("Failed to fetch trip data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard" data-theme={theme}>
      <header className="dashboard-header">
        <div className="brand">
          <div className="brand-logo-wrap">
            <img src="/logo.png" alt="Amin Logistics Logo" className="brand-logo" />
          </div>
          <div>
            <p className="brand-name">Amin Logistics</p>
            <p className="brand-tagline">Riding smiles, one journey at a time</p>
          </div>
        </div>
        <button
          type="button"
          className="theme-toggle"
          onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
        >
          {theme === "dark" ? <SunIcon /> : <MoonIcon />}
          {theme === "dark" ? "Light" : "Dark"}
        </button>
      </header>

      {hasSearched && tripList.length > 0 && (
        <div className="stat-grid">
          <StatCard label="Total mileage" value={stats.totalMileage.toFixed(2)} />
          <StatCard label="Completed trips" value={stats.completedCount} />
          <StatCard label="Total no shows" value={stats.noShowCount} tone="warning" />
          <StatCard label="Total cancelled" value={stats.cancelledCount} tone="danger" />
          <StatCard label="Total rate" value={`$${stats.totalRate.toFixed(2)}`} />
        </div>
      )}

      <div className="filter-card">
        <div className="field field-grow">
          <label htmlFor="driverName">Driver name</label>
          <input
            id="driverName"
            type="text"
            placeholder="Search by name"
            value={driverName}
            onChange={(e) => setDriverName(e.target.value)}
          />
        </div>
        <DateRangeField
          startDate={startDate}
          endDate={endDate}
          onChange={(start, end) => {
            setStartDate(start);
            setEndDate(end);
          }}
        />
        <div className="status-chips" role="group" aria-label="Status filter">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.label}
              type="button"
              className={`chip${status === f.value ? " chip-active" : ""}`}
              onClick={() => setStatus(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button type="button" className="search-btn" onClick={fetchTrips} disabled={loading}>
          {loading ? <span className="spinner" aria-hidden="true" /> : <SearchIcon />}
          {loading ? "Searching..." : "Search"}
        </button>
      </div>

      {dateError && <div className="banner banner-danger">{dateError}</div>}
      {fetchError && <div className="banner banner-danger">{fetchError}</div>}

      {hasSearched && tripList.length > 0 && (
        <p className="hint-text">
          Cancelled trips are counted above but excluded from the list below.
        </p>
      )}

      <TripTable trips={stats.listTrips} hasSearched={hasSearched} />
    </div>
  );
};

export default App;
